import type {
  MobileOtpType,
  ResendParams,
  User,
  UserAttributes,
  UserMetadata,
} from '@supabase/supabase-js'
import { useMutation } from '@tanstack/react-query'
import { supabase } from '~/lib/supabase'

export type SendOtpInput = { email: string } | { phone: string }

export function useSendOtp({ allowAccountCreation }: { allowAccountCreation?: boolean }) {
  return useMutation({
    mutationFn: async (input: SendOtpInput) => {
      const authProps =
        'email' in input
          ? {
              email: input.email,
              options: {
                shouldCreateUser: allowAccountCreation ?? true,
              },
            }
          : {
              phone: input.phone,
              options: {
                shouldCreateUser: allowAccountCreation ?? true,
                channel: 'sms' as const,
              },
            }

      if (!authProps) {
        // eslint-disable-next-line i18next/no-literal-string
        throw new Error('Provide exactly one of email or phone')
      }

      const { error } = await supabase.auth.signInWithOtp(authProps)

      if (error) {
        throw error
      }

      return true
    },
  })
}

export type VerifyOtpInput = { token: string } & (
  | {
      email: string
      type: 'email' | 'email_change'
    }
  | {
      phone: string
      type: 'sms' | 'phone_change'
    }
)

export function useVerifyOtp(onSuccess?: (user: User, variables: VerifyOtpInput) => unknown) {
  return useMutation({
    mutationFn: async (input: VerifyOtpInput) => {
      const verifyProps =
        'email' in input
          ? {
              email: input.email,
              token: input.token,
              type: input.type,
            }
          : {
              phone: input.phone,
              token: input.token,
              type: input.type,
            }

      const { data, error } = await supabase.auth.verifyOtp(verifyProps)

      if (error) {
        throw error
      }

      return data.user as User
    },
    onSuccess,
  })
}

export function useChangeAuthContact() {
  return useMutation({
    mutationFn: async (input: Partial<SendOtpInput> & { metadata?: UserMetadata }) => {
      const updateProps: UserAttributes | null =
        'email' in input
          ? {
              email: input.email ?? undefined,
              data: input.metadata ?? undefined,
            }
          : 'phone' in input
            ? {
                phone: input.phone ?? undefined,
                data: input.metadata ?? undefined,
              }
            : null

      if (!updateProps) {
        // eslint-disable-next-line i18next/no-literal-string
        throw new Error('Provide email or phone')
      }

      const { error } = await supabase.auth.updateUser(updateProps)

      if (error) {
        throw error
      }

      return true
    },
  })
}

export type ResendOtpInput =
  | {
      email: string
      type: 'signup' | 'email_change'
    }
  | {
      phone: string
      type: MobileOtpType
    }

export function useResendOtp() {
  return useMutation({
    mutationFn: async (input: ResendOtpInput) => {
      const resendProps: ResendParams =
        'email' in input
          ? {
              email: input.email,
              type: input.type,
            }
          : {
              phone: input.phone,
              type: input.type,
            }

      const result = await supabase.auth.resend(resendProps)

      if (result.error) {
        throw result.error
      }

      return true
    },
  })
}
